/**
 * Lift2Fit — admin: Businesses (`#/admin/businesses`).
 *
 * The CRM side of the platform: every account holding the coach role, with its
 * plan, seats used against the plan's limit, active clients, slots, bookings
 * and the money in its ledger. Aggregates only — a business's client list is
 * never read here.
 */
import { api } from '@/lib/api';
import { formatDate } from '@/lib/format';
import { AlertIcon, MonitorIcon } from '@/lib/icons';
import {
  AppLinkButton,
  EmptyState,
  LoadingPanel,
  Panel,
  ScreenHeader,
  StatusChip,
} from '../components';
import { formatMoney } from '../crm/shared';
import { useAsync } from '../hooks';
import { ADMIN_EYEBROW, coachPlanLabel, formatCount, seatLimitLabel } from './shared';

export function AdminBusinessesScreen() {
  const state = useAsync(() => api.adminBusinesses(), []);
  const businesses = state.data?.data ?? [];

  if (state.loading && !state.data) {
    return <LoadingPanel label="Loading coaching businesses" rows={5} />;
  }

  if (!state.data) {
    return (
      <EmptyState
        title="We could not read the businesses"
        body={state.error ?? 'The admin API did not answer. Sign in as an administrator and try again.'}
        icon={<AlertIcon className="h-8 w-8 text-accent" />}
        action={
          <AppLinkButton to="/admin" variant="outline">
            Back to the overview
          </AppLinkButton>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-6" data-testid="admin-businesses">
      <ScreenHeader
        eyebrow={`${ADMIN_EYEBROW} · CRM`}
        title="Businesses"
        badge={<StatusChip tone="danger">Founder only</StatusChip>}
        lead="Every coaching business: its plan, the seats it uses against the plan limit, its clients, slots, bookings and revenue."
        actions={
          <AppLinkButton to="/admin/accounts?role=coach" variant="outline">
            Accounts
          </AppLinkButton>
        }
      />

      <Panel
        title={`${formatCount(state.data.total)} business(es)`}
        subtitle="One entity sized by client count — a solo coach or a club of eight is the same product."
        testId="admin-businesses-table"
      >
        {businesses.length === 0 ? (
          <EmptyState
            title="No coaching businesses yet"
            body="An account appears here once it holds the coach role. Grant it from an account's page."
            icon={<MonitorIcon className="h-8 w-8 text-accent" />}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] border-collapse text-left">
              <thead>
                <tr className="border-b border-hairline text-[10px] uppercase tracking-[0.1em] text-ink-muted">
                  <th className="py-2 pr-3 font-bold">Business</th>
                  <th className="py-2 pr-3 font-bold">Plan</th>
                  <th className="py-2 pr-3 font-bold">Seats</th>
                  <th className="py-2 pr-3 font-bold">Clients</th>
                  <th className="py-2 pr-3 font-bold">Slots</th>
                  <th className="py-2 pr-3 font-bold">Bookings</th>
                  <th className="py-2 pr-3 font-bold">Paid</th>
                  <th className="py-2 pr-3 font-bold">Due</th>
                  <th className="py-2 font-bold">Last activity</th>
                </tr>
              </thead>
              <tbody>
                {businesses.map((business) => (
                  <tr
                    key={business.userId}
                    data-testid={`admin-business-row-${business.userId}`}
                    className="border-b border-hairline last:border-b-0 align-top"
                  >
                    <td className="py-3 pr-3">
                      <span className="flex min-w-0 flex-col">
                        <span className="text-[13px] font-bold text-ink">{business.displayName}</span>
                        <span className="text-[12px] text-ink-muted">{business.email}</span>
                        <span className="text-[11px] text-ink-muted">created {formatDate(business.createdAt)}</span>
                      </span>
                    </td>
                    <td className="py-3 pr-3 text-[12px] text-ink">{coachPlanLabel(business.plan)}</td>
                    <td
                      data-testid={`admin-business-seats-${business.userId}`}
                      className="l2f-tabular py-3 pr-3 font-mono text-[12px] text-ink"
                    >
                      {formatCount(business.seatsUsed)} / {seatLimitLabel(business.seatsLimit)}
                      {business.pendingInvites > 0 ? (
                        <span className="ml-1 text-ink-muted">({formatCount(business.pendingInvites)} pending)</span>
                      ) : null}
                    </td>
                    <td className="l2f-tabular py-3 pr-3 font-mono text-[12px] text-ink">{formatCount(business.clients)}</td>
                    <td className="l2f-tabular py-3 pr-3 font-mono text-[12px] text-ink">{formatCount(business.openSlots)}</td>
                    <td className="l2f-tabular py-3 pr-3 font-mono text-[12px] text-ink">
                      {formatCount(business.bookings)}
                      <span className="ml-1 text-ink-muted">({formatCount(business.attended)} attended)</span>
                    </td>
                    <td
                      data-testid={`admin-business-revenue-${business.userId}`}
                      className="l2f-tabular py-3 pr-3 font-mono text-[12px] text-success"
                    >
                      {formatMoney(business.paid, business.currency)}
                    </td>
                    <td className="l2f-tabular py-3 pr-3 font-mono text-[12px] text-warning">
                      {formatMoney(business.due, business.currency)}
                    </td>
                    <td className="py-3 text-[12px] text-ink-muted">
                      {business.lastActivityAt ? formatDate(business.lastActivityAt) : 'never'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
